import { fromBaseClone, fromBaseRecord, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBaseNpcMetadata } from './from-base-npc-stages';
import { createFromBaseNpcGrantContext, createFromBaseNpcSourceTypeReader, constructNpcIntegersFromBase } from './from-base-npc-stages';
import { constructNpcStaticInventoryFromBase } from './from-base-npc-static-inventory';
import { constructNpcForcedSkillsFromBase } from './from-base-npc-forced-skills';
import { constructNpcFullSkillEntriesFromBase } from './from-base-npc-full-skills';
import { constructNpcFeaturesFromBase } from './from-base-npc-features';
import { constructNpcArmorClassFromBase } from './from-base-npc-ac';
import { createPassivePermanentStatusCompiler } from './passive-permanent-status';
import { compileCreatureSenses } from './creature-senses-compile';
import { resolveCreatureShapeshiftForm } from './creature-shapeshift-form';
import { compileNpcSpeeds } from './from-base-npc-speeds';
import { compileCreatureAttacks, pristineFragments, sourceRow } from './creature-attacks';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId, readItemScalarLayerSlot } from './layer-cell-readers';
import { scoreCarrierIntegerLayer } from './formula-variable-resolution';
import { createDynamicIntegerSourceCollector } from './dynamic-integer-sources';
import { capInventorySourceRows } from './computed-persistence';
import { createCreatureEquippedCountsCompiler } from './creature-equipped-counts';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { foldCreatureReachSources } from './creature-reach';
import { defenseWriter } from './from-base-npc-defenses';
import { DEFENSE_SLOTS } from './creature-defense-delta';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import type { ItemFieldRow } from './item-field-reader';
import { relocateCreatureDerivedToComputed } from './creature-derived-relocation';

/** ft_finalize_item_arr_post's corrective creature window, after expansion and
 * mirror. Every cluster consumes this same locally constructed host. */
export function constructNpcPostFromBase(base: Row, metadata: FromBaseNpcMetadata, input: Row): Row {
    let host = fromBaseClone(input);
    let context = createFromBaseNpcGrantContext(base, metadata, host, 'catalog-lean-normalization');
    if (!phpJsonTruthy(context.readers.itemField(fromBaseRecord(host.encounter_creatures) ? host.encounter_creatures : null, 'encounter_creature_id'))) {
        delete host.inventory;
        host = constructNpcStaticInventoryFromBase(base, metadata, metadata.inventoryMetadata!, host, false);
        context = createFromBaseNpcGrantContext(base, metadata, host, 'catalog-lean-normalization');
    }
    host = createPassivePermanentStatusCompiler(context.deps).initialize(host, true) as Row;
    const cache = fromBaseRecord(host.computed) && fromBaseRecord(host.computed._cache) ? host.computed._cache : {};
    if (!phpJsonTruthy(context.readers.itemField(fromBaseRecord(host.encounter_creatures) ? host.encounter_creatures : null, 'encounter_creature_id')))
        cache.static_equipped_projection_v = 1;
    delete cache.ac_values_done; delete host.__ac_values_done;
    host = constructNpcIntegersFromBase(base, metadata, metadata.inventoryMetadata!, host);
    const forced = constructNpcForcedSkillsFromBase(base, metadata, host);
    host = constructNpcFullSkillEntriesFromBase(base, metadata, forced.row, forced.forced).row;
    host = constructNpcFeaturesFromBase(base, metadata, host);
    host = constructNpcArmorClassFromBase(base, metadata, host);
    context = createFromBaseNpcGrantContext(base, metadata, host, 'catalog-lean-normalization');
    const { readers, definition, deps } = context;
    const shared = { ...deps, inventoryAbilityTypes: metadata.rollup.inventoryAbilityTypes,
        queryItemType: createFromBaseNpcSourceTypeReader(base, metadata, host) };
    const creature = ((host.computed ??= {}) as Row).creature as Row;
    const selected = resolveCreatureShapeshiftForm(host, shared);
    if (!selected.ok) throw new FromBaseStageMissing('catalog-lean-normalization', `unported:npc:${selected.reason}`);
    const form = selected.form, body = form ?? host;
    const senses = compileCreatureSenses(host, shared, form, true), speeds = compileNpcSpeeds(host, shared, { form }), attacks = compileCreatureAttacks(host, shared, form);
    for (const result of [senses, speeds, attacks]) if (!result.ok) throw new FromBaseStageMissing('catalog-lean-normalization', result.reason);
    if (senses.ok) { if (senses.senses) creature.senses = senses.senses; else delete creature.senses; }
    if (speeds.ok) {
        const kept = Object.fromEntries(Object.entries(speeds.speeds).filter(([, raw]) => {
            if (!fromBaseRecord(raw)) return true;
            const total = readers.itemField(raw, 'total'), base = readers.itemField(raw, 'base'), locked = readers.itemField(raw, 'locked');
            // Native ft_compute_persist elides a zero even with force_wrapper,
            // when it has no provenance, lock, cap or extra fields.
            return total !== 0 || base !== 0 || locked !== false || Object.keys(raw).some(key => !['total', 'base', 'locked'].includes(key));
        }));
        if (Object.keys(kept).length) creature.speeds = kept; else delete creature.speeds;
    }
    if (attacks.ok) {
        if (attacks.attacks) creature.attacks = attacks.attacks; else delete creature.attacks;
        if (attacks.combat) creature.combat = attacks.combat;
    }
    const providers = createCatalogDefaultProviderReader({ catalogRows: metadata.layerCatalog, nodeItemId: readNodeItemId, resolveCarrierRef: definition });
    const sizeId = readItemScalarLayerSlot(readers.layerValue(host, 'size'));
    const size = metadata.resources.sizes.find(row => readers.itemField(row, 'item_id') === sizeId) ?? null;
    const score = (fragments: unknown[]) => scoreCarrierIntegerLayer({ item_layer_types: { reach: fragments } }, 'reach', 0, false, metadata.preliminary, readers);
    const sizeReach = score(size ? [readers.layerValue(size, 'reach')] : []);
    const sources: Row[] = sizeReach ? [sourceRow('size', 'Size', sizeReach, sizeId, 'size')] : [];
    for (const row of [body, ...providers.providers(body)]) {
        const value = score(pristineFragments(row, 'reach')); if (!value) continue;
        const own = row === body, type = own ? 'host' : phpJsonString(readers.itemField(row, 'item_type') ?? 'carrier');
        sources.push(sourceRow(type, phpJsonString(readers.itemField(row, 'item_name') ?? (own ? 'Host' : '')), value, phpJsonString(readers.itemField(row, 'item_id') ?? ''), type));
    }
    const [dynamic] = createDynamicIntegerSourceCollector(shared).dynamic(host, 'reach', true, true, form);
    sources.push(...dynamic);
    const [capped] = capInventorySourceRows(sources);
    const reach = foldCreatureReachSources({ sizeReach, sources: Object.values(capped) as import('../types').CreatureReachSource[] });
    if (reach) creature.reach = reach; else delete creature.reach;
    const lists: Row[] = [];
    for (const [index, key] of ['spell_prepared_1', 'spell_prepared_2', 'spell_prepared_3'].entries()) {
        const levels = new Map<number, Row[]>();
        for (const entry of Object.values(readers.layerEntries(host, key))) {
            const id = readItemScalarLayerSlot(entry), spell = id ? definition(id) : null;
            if (!spell) continue;
            const level = phpJsonInt(readers.layerValue(spell, 'spell_level'));
            if (!levels.has(level)) levels.set(level, []);
            levels.get(level)!.push({ item_id: id, item_name: readers.itemField(spell, 'item_name') ?? '' });
        }
        if (levels.size) lists.push({ list: index + 1, levels: [...levels.keys()].sort((a, b) => a - b).map(level => ({ level, spells: levels.get(level) })) });
    }
    if (lists.length) creature.spells_prepared = { lists }; else delete creature.spells_prepared;
    const writer = defenseWriter(host, readers), seen = new Set<string>([phpJsonString(readers.itemField(host, 'item_id'))]);
    const keys = Object.keys(DEFENSE_SLOTS);
    function add(row: ItemFieldRow, meta: Row): void {
        const layers = fromBaseRecord(row) && fromBaseRecord(row.item_layer_types) ? row.item_layer_types : {};
        for (const key of keys) {
            const raw = layers[key], entries = raw !== null && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, '0') ? Object.values(raw) : raw == null ? [] : [raw];
            for (const entry of entries) {
                const id = fromBaseRecord(entry) && entry.damage_type != null ? phpJsonString(readers.itemField(entry, 'damage_type')) : readLayerListEntryUuid(entry);
                if (id) writer.add(key, id, meta);
            }
        }
    }
    function fold(row: ItemFieldRow): void {
        for (const provider of providers.providers(row)) {
            const id = readNodeItemId(provider); if (id && seen.has(id)) continue;
            if (id) seen.add(id);
            const type = phpJsonString(readers.itemField(provider, 'item_type') ?? 'carrier');
            add(provider, { source_item_id: id, source_name: readers.itemField(provider, 'item_name') ?? 'Creature classification', source_kind: type });
            for (const key of ['feat', 'passive_ability']) {
                const layers = fromBaseRecord(provider) && fromBaseRecord(provider.item_layer_types) ? provider.item_layer_types : {};
                const raw = layers[key], entries = raw !== null && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, '0') ? Object.values(raw) : raw == null ? [] : [raw];
                for (const entry of entries) {
                    const abilityId = readLayerListEntryUuid(entry); if (!abilityId || seen.has(abilityId)) continue;
                    const ability = fromBaseRecord(entry) && fromBaseRecord(entry.item_arr) ? entry.item_arr : definition(abilityId);
                    if (!ability || !fromBaseRecord(ability.item_layer_types) || !Object.keys(ability.item_layer_types).length) continue;
                    if (['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(key => readers.layerValue(ability, key) !== null)) continue;
                    seen.add(abilityId);
                    if (!context.conditions.check(ability, host)) continue;
                    add(ability, { source_item_id: readers.itemField(ability, 'item_id') ?? abilityId, source_name: readers.itemField(ability, 'item_name') ?? 'Granted ability', source_kind: key });
                }
            }
            fold(provider);
        }
    }
    fold(host);
    host = createCreatureEquippedCountsCompiler({ ...deps, ...metadata.equippedCounts })(host) as Row;
    relocateCreatureDerivedToComputed(host, readers);
    return host;
}
